import "./Admin.css";

import { useCallback, useEffect, useMemo, useState } from "react";

import api from "../../services/api";
import logo from "../../assets/images/logo.png";

import ProductsAdmin from "./components/ProductsAdmin";
import CustomersAdmin from "./components/CustomersAdmin";
import MessagesAdmin from "./components/MessagesAdmin";
import ReportsAdmin from "./components/ReportsAdmin";
import ReviewsAdmin from "./components/ReviewsAdmin";
import SettingsAdmin from "./components/SettingsAdmin";

import {
  FaDollarSign,
  FaUsers,
  FaShoppingBag,
  FaStar,
  FaTruck,
  FaClipboardList,
  FaBoxOpen,
  FaCommentDots,
  FaHome,
  FaCog,
  FaSignOutAlt,
  FaFileExcel,
  FaEye,
  FaSearch,
  FaBars,
  FaTimes,
  FaSyncAlt,
  FaShieldAlt,
  FaChevronLeft,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";

const TABS = [
  {
    id: "dashboard",
    label: "לוח ראשי",
    subtitle: "תמונת מצב של העסק",
    icon: FaHome,
  },
  {
    id: "orders",
    label: "הזמנות",
    subtitle: "מעקב ועדכון הזמנות",
    icon: FaShoppingBag,
  },
  {
    id: "products",
    label: "מוצרים",
    subtitle: "מלאי וקטלוג",
    icon: FaBoxOpen,
  },
  {
    id: "customers",
    label: "לקוחות",
    subtitle: "פרטי לקוחות",
    icon: FaUsers,
  },
  {
    id: "messages",
    label: "הודעות",
    subtitle: "פניות מהאתר",
    icon: FaCommentDots,
  },
  {
    id: "reports",
    label: "דוחות",
    subtitle: "נתונים וייצוא Excel",
    icon: FaFileExcel,
  },
  {
    id: "settings",
    label: "הגדרות",
    subtitle: "פרטי האתר והמשלוח",
    icon: FaCog,
  },
  {
    id: "reviews",
    label: "ביקורות",
    subtitle: "אישור והצגת ביקורות",
    icon: FaStar,
  },
];

const STATUS_LABELS = {
  Pending: "התקבלה",
  Paid: "התשלום אושר",
  Preparing: "בהכנה",
  Shipped: "במשלוח",
  Delivered: "נמסרה",
  Cancelled: "בוטלה",
};

const PAGE_META = {
  dashboard: {
    eyebrow: "סקירה כללית",
    title: "לוח הניהול",
    description: "כל מה שצריך כדי לנהל את Cervica במקום אחד.",
  },
  orders: {
    eyebrow: "ניהול הזמנות",
    title: "הזמנות",
    description: "חיפוש, סינון, צפייה בפרטים ועדכון סטטוס בזמן אמת.",
  },
  products: {
    eyebrow: "קטלוג ומלאי",
    title: "מוצרים",
    description: "ניהול מוצרים, מחירים, מלאי ותמונות בצורה פשוטה.",
  },
  customers: {
    eyebrow: "ניהול לקוחות",
    title: "לקוחות",
    description: "כל פרטי הלקוחות והיסטוריית ההצטרפות במקום מסודר.",
  },
  messages: {
    eyebrow: "שירות לקוחות",
    title: "הודעות",
    description: "קריאה, מענה וניהול פניות שהתקבלו דרך האתר.",
  },
  reports: {
    eyebrow: "ביצועים ונתונים",
    title: "דוחות",
    description: "דוחות חודשיים, סיכומי מכירות וייצוא מהיר ל-Excel.",
  },
  settings: {
    eyebrow: "הגדרות מערכת",
    title: "הגדרות האתר",
    description: "עדכון פרטי קשר, מחיר משלוח ופרטי העסק המוצגים באתר.",
  },
  reviews: {
    eyebrow: "ניהול מוניטין",
    title: "ביקורות",
    description: "אישור, הסתרה והבלטה של ביקורות לקוחות.",
  },
};

const translatePayment = (value) =>
  ({
    cash: "מזומן",
    visa: "כרטיס אשראי",
    paypal: "PayPal",
  })[value] || value;

const translateDelivery = (value) =>
  value === "delivery" ? "משלוח" : "איסוף עצמי";

function getStatusInfo(status) {
  const suffix = STATUS_LABELS[status] ? status.toLowerCase() : "default";

  return {
    label: STATUS_LABELS[status] || status,
    className: "status-" + suffix,
    rowClass: "row-" + suffix,
  };
}

function getStoredAdminName() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.name || "מנהל המערכת";
  } catch {
    return "מנהל המערכת";
  }
}

function Admin() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("All");
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const adminName = useMemo(() => getStoredAdminName(), []);
  const pageMeta = PAGE_META[activeTab] || PAGE_META.dashboard;

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [dashboardResponse, ordersResponse] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/orders"),
      ]);

      setDashboard(dashboardResponse.data);
      setAllOrders(ordersResponse.data);
      setError("");
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {
      setError("טעינת הנתונים נכשלה.");
    });
  }, [refresh]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [activeTab]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put("/orders/" + orderId + "/status", { status });

      setSelectedOrder((previous) =>
        previous?.order_id === orderId ? { ...previous, status } : previous,
      );

      await refresh();
    } catch {
      setError("עדכון הנתונים לא הושלם. רעננו את רשימת ההזמנות.");
    }
  };

  const openOrderDetails = async (orderId) => {
    try {
      const { data } = await api.get("/orders/" + orderId + "/details");
      setSelectedOrder(data);
    } catch {
      setError("טעינת פרטי ההזמנה נכשלה.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user");
    window.location.assign("/login");
  };

  const search = orderSearch.trim().toLowerCase();

  const filteredOrders = allOrders.filter((order) => {
    const matchesSearch = [
      order.order_id,
      String(order.first_name || "") + " " + String(order.last_name || ""),
      order.phone,
      order.email,
    ].some((value) =>
      String(value ?? "")
        .toLowerCase()
        .includes(search),
    );

    return (
      matchesSearch &&
      (orderStatusFilter === "All" || order.status === orderStatusFilter)
    );
  });

  if (!dashboard) {
    return (
      <div className="admin-loading-screen" dir="rtl">
        <div className="admin-loading-logo">
          <img src={logo} alt="Cervica" />
        </div>
        <div className="admin-loader" />
        <p>{error || "טוען את לוח הניהול..."}</p>

        {error && (
          <button
            onClick={() =>
              refresh().catch(() => setError("טעינת הנתונים נכשלה."))
            }
          >
            ניסיון נוסף
          </button>
        )}
      </div>
    );
  }

  const pendingCount = allOrders.filter((order) => order.status === "Pending").length;
  const cards = [
    {
      icon: FaDollarSign,
      value: `₪ ${dashboard.revenue}`,
      label: "שווי הזמנות פעילות",
      className: "revenue",
      hint: "סכום מעודכן מהמערכת",
    },
    {
      icon: FaUsers,
      value: dashboard.totalCustomers,
      label: "לקוחות",
      className: "customers",
      hint: "לקוחות רשומים",
    },
    {
      icon: FaShoppingBag,
      value: dashboard.totalOrders,
      label: "הזמנות",
      className: "orders",
      hint: pendingCount ? `${pendingCount} ממתינות לטיפול` : "אין הזמנות ממתינות",
    },
    {
      icon: FaBoxOpen,
      value: dashboard.preparing,
      label: "בהכנה",
      className: "preparing",
      hint: "בטיפול כרגע",
    },
    {
      icon: FaTruck,
      value: dashboard.shipped,
      label: "במשלוח",
      className: "shipped",
      hint: "בדרך ללקוחות",
    },
    {
      icon: FaCheckCircle,
      value: dashboard.delivered,
      label: "נמסרו",
      className: "delivered",
      hint: "הושלמו בהצלחה",
    },
  ];

  return (
    <main className="admin-shell">
      <div
        className={`admin-sidebar-backdrop ${sidebarOpen ? "visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      <aside className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="admin-brand-row">
          <div className="admin-brand-logo">
            <img src={logo} alt="Cervica" />
          </div>

          <button
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="סגירת תפריט"
          >
            <FaTimes />
          </button>
        </div>

        <div className="admin-sidebar-label">ADMIN WORKSPACE</div>

        <nav className="admin-nav" aria-label="ניווט מנהל">
          {TABS.map(({ id, label, subtitle, icon: Icon }) => (
            <button
              key={id}
              className={activeTab === id ? "active" : ""}
              onClick={() => setActiveTab(id)}
            >
              <span className="admin-nav-icon">
                <Icon />
              </span>
              <span className="admin-nav-copy">
                <strong>{label}</strong>
                <small>{subtitle}</small>
              </span>
              <FaChevronLeft className="admin-nav-arrow" />
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-security-card">
            <span className="admin-security-icon">
              <FaShieldAlt />
            </span>
            <div>
              <strong>סביבת ניהול מאובטחת</strong>
              <small>
                <span className="online-dot" /> המערכת פעילה
              </small>
            </div>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt />
            התנתקות מהמערכת
          </button>
        </div>
      </aside>

      <section className="admin-page">
        <header className="admin-topbar">
          <div className="admin-title-wrap">
            <button
              className="mobile-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="פתיחת תפריט"
            >
              <FaBars />
            </button>

            <div>
              <span className="admin-page-eyebrow">{pageMeta.eyebrow}</span>
              <h1>{pageMeta.title}</h1>
              <p>{pageMeta.description}</p>
            </div>
          </div>

          <div className="admin-topbar-actions">
            <button
              className="admin-refresh-btn"
              onClick={() => refresh().catch(() => setError("טעינת הנתונים נכשלה."))}
              disabled={isRefreshing}
            >
              <FaSyncAlt className={isRefreshing ? "spinning" : ""} />
              <span>{isRefreshing ? "מרענן..." : "רענון"}</span>
            </button>

            <div className="admin-profile-chip">
              <div className="admin-avatar">
                {adminName.trim().charAt(0) || "מ"}
              </div>
              <div>
                <strong>{adminName}</strong>
                <small>מנהל מערכת</small>
              </div>
            </div>
          </div>
        </header>

        {error && <p className="admin-error-banner" role="alert">{error}</p>}

        {activeTab === "dashboard" && (
          <>
            <section className="admin-overview-hero">
              <div className="admin-overview-copy">
                <span className="admin-hero-kicker">
                  <FaShieldAlt />
                  מרכז הבקרה של Cervica
                </span>
                <h2>ברוכים הבאים, {adminName}</h2>
                <p>
                  כאן אפשר לראות את מצב העסק, לעבור במהירות להזמנות ולנהל את
                  המלאי בלי לחפש בין מסכים מיותרים.
                </p>

                <div className="admin-hero-actions">
                  <button onClick={() => setActiveTab("orders")}>
                    צפייה בהזמנות
                    <FaChevronLeft />
                  </button>
                  <button className="secondary" onClick={() => setActiveTab("products")}>
                    ניהול מלאי
                  </button>
                </div>
              </div>

              <div className="admin-overview-status">
                <div className="overview-ring">
                  <span>{dashboard.totalOrders}</span>
                  <small>סה״כ הזמנות</small>
                </div>

                <div className="overview-mini-list">
                  <div>
                    <span className="overview-mini-icon pending"><FaClock /></span>
                    <div><strong>{pendingCount}</strong><small>ממתינות לטיפול</small></div>
                  </div>
                  <div>
                    <span className="overview-mini-icon shipped"><FaTruck /></span>
                    <div><strong>{dashboard.shipped}</strong><small>במשלוח</small></div>
                  </div>
                  <div>
                    <span className="overview-mini-icon delivered"><FaCheckCircle /></span>
                    <div><strong>{dashboard.delivered}</strong><small>נמסרו</small></div>
                  </div>
                </div>
              </div>
            </section>

            <section className="dashboard-cards">
              {cards.map(({ icon: Icon, value, label, className, hint }) => (
                <article className={`dashboard-card ${className}`} key={label}>
                  <div className="dashboard-card-top">
                    <span className="dashboard-card-icon"><Icon /></span>
                    <span className="live-badge">LIVE</span>
                  </div>
                  <h2>{value}</h2>
                  <strong>{label}</strong>
                  <small>{hint}</small>
                </article>
              ))}
            </section>

            <OrdersTable
              orders={dashboard.recentOrders}
              title="הזמנות אחרונות"
              updateStatus={updateStatus}
              openOrderDetails={openOrderDetails}
            />
          </>
        )}

        {activeTab === "orders" && (
          <OrdersTable
            orders={filteredOrders}
            title="כל ההזמנות"
            updateStatus={updateStatus}
            openOrderDetails={openOrderDetails}
            showFilters
            orderSearch={orderSearch}
            setOrderSearch={setOrderSearch}
            orderStatusFilter={orderStatusFilter}
            setOrderStatusFilter={setOrderStatusFilter}
          />
        )}

        {activeTab === "products" && <ProductsAdmin />}
        {activeTab === "customers" && <CustomersAdmin />}
        {activeTab === "messages" && <MessagesAdmin />}
        {activeTab === "reports" && <ReportsAdmin />}
        {activeTab === "settings" && <SettingsAdmin />}
        {activeTab === "reviews" && <ReviewsAdmin />}
      </section>

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          updateStatus={updateStatus}
        />
      )}
    </main>
  );
}

function StatusSelect({ order, updateStatus }) {
  const info = getStatusInfo(order.status);

  return (
    <select
      aria-label="עדכון סטטוס הזמנה"
      value={order.status}
      className={"status-select " + info.className}
      onChange={(event) => updateStatus(order.order_id, event.target.value)}
    >
      {Object.entries(STATUS_LABELS).map(([value, label]) => (
        <option key={value} value={value}>{label}</option>
      ))}
    </select>
  );
}

function OrdersTable({
  orders = [],
  title,
  updateStatus,
  openOrderDetails,
  showFilters = false,
  orderSearch,
  setOrderSearch,
  orderStatusFilter,
  setOrderStatusFilter,
}) {
  return (
    <section className="dashboard-section orders-section">
      <div className="section-title">
        <div className="section-title-main">
          <span className="section-icon"><FaClipboardList /></span>
          <div>
            <h2>{title}</h2>
            <p>ניהול ומעקב אחרי הזמנות הלקוחות</p>
          </div>
        </div>
        <span className="section-count">{orders.length} רשומות</span>
      </div>

      {showFilters && (
        <div className="orders-toolbar">
          <div className="orders-search">
            <FaSearch />
            <input
              type="text"
              aria-label="חיפוש הזמנות"
              placeholder="חיפוש לפי שם, טלפון, אימייל או מספר הזמנה"
              value={orderSearch}
              onChange={(event) => setOrderSearch(event.target.value)}
            />
          </div>

          <div className="orders-filters">
            {Object.entries({ All: "הכל", ...STATUS_LABELS }).map(([value, label]) => (
              <button
                key={value}
                className={orderStatusFilter === value ? "active" : ""}
                onClick={() => setOrderStatusFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="admin-table-wrap">
        <table>
          <thead>
            <tr>
              <th>מספר הזמנה</th>
              <th>לקוח</th>
              <th>טלפון</th>
              <th>סכום</th>
              <th>קבלה</th>
              <th>תשלום</th>
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>

          <tbody>
            {!orders.length ? (
              <tr><td colSpan="8" className="empty-table-cell">אין הזמנות להצגה</td></tr>
            ) : (
              orders.map((order) => {
                const info = getStatusInfo(order.status);

                return (
                  <tr key={order.order_id} className={"order-row " + info.rowClass}>
                    <td><span className="order-number">#{order.order_id}</span></td>
                    <td><strong>{order.first_name} {order.last_name}</strong></td>
                    <td>{order.phone}</td>
                    <td><strong>₪ {order.total_price}</strong></td>
                    <td>{translateDelivery(order.delivery_method)}</td>
                    <td>{translatePayment(order.payment_method)}</td>
                    <td>
                      <div className="status-cell">
                        <span className={"order-status-pill " + info.className}>{info.label}</span>
                        <StatusSelect order={order} updateStatus={updateStatus} />
                      </div>
                    </td>
                    <td>
                      <button className="details-btn" onClick={() => openOrderDetails(order.order_id)}>
                        <FaEye />
                        פרטים
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OrderDetailsModal({ order, onClose, updateStatus }) {
  const info = getStatusInfo(order.status);

  const fields = [
    ["לקוח", String(order.first_name || "") + " " + String(order.last_name || "")],
    ["טלפון", order.phone],
    ["אימייל", order.email],
    ["עיר", order.city],
    ["כתובת", order.delivery_address],
    ["מיקוד", order.postal_code],
    ["שיטת קבלה", translateDelivery(order.delivery_method)],
    ["שיטת תשלום", translatePayment(order.payment_method)],
  ];

  return (
    <div className="order-details-modal" role="dialog" aria-modal="true" aria-label={"פרטי הזמנה " + order.order_id}>
      <div className="order-details-card">
        <div className="order-details-header">
          <div>
            <span className="modal-eyebrow">ORDER DETAILS</span>
            <h2>פרטי הזמנה #{order.order_id}</h2>
            <span className={"order-status-pill " + info.className}>{info.label}</span>
          </div>

          <button className="modal-close-btn" onClick={onClose} aria-label="סגירה"><FaTimes /></button>
        </div>

        <div className="order-details-grid">
          {fields.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value || "-"}</strong>
            </div>
          ))}
        </div>

        <div className="order-status-update">
          <div><span>סטטוס הזמנה</span><small>שינוי הסטטוס נשמר מיד במערכת</small></div>
          <StatusSelect order={order} updateStatus={updateStatus} />
        </div>

        <div className="modal-table-title">
          <h3>מוצרים בהזמנה</h3>
          <span>{order.items?.length || 0} פריטים</span>
        </div>

        <div className="admin-table-wrap modal-table-wrap">
          <table>
            <thead><tr><th>מוצר</th><th>כמות</th><th>מחיר</th><th>סה״כ</th></tr></thead>
            <tbody>
              {!order.items?.length ? (
                <tr><td colSpan="4" className="empty-table-cell">אין מוצרים להצגה</td></tr>
              ) : (
                order.items.map((item) => (
                  <tr key={item.order_item_id}>
                    <td><strong>{item.product_name}</strong></td>
                    <td>{item.quantity}</td>
                    <td>₪ {Number(item.price).toFixed(2)}</td>
                    <td><strong>₪ {(Number(item.price) * Number(item.quantity)).toFixed(2)}</strong></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="order-total-box">
          <span>סה״כ לתשלום</span>
          <strong>₪ {order.total_price}</strong>
        </div>
      </div>
    </div>
  );
}

export default Admin;
